import { css } from '@emotion/react';
import {
  faCaretUp, faCheck, faPencil, faTrash, faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, memo, useState,
} from 'react';
import { ACCORDION_HEADER_SIZE_MEDIUM } from '~/_shared/baseComponents/accordion';
import { ButtonComponent } from '~/_shared/baseComponents/buttons/button/button.component';
import { ButtonStyle } from '~/_shared/baseComponents/buttons/button/button.types';
import {
  RoundButtonComponent,
  RoundButtonSize,
} from '~/_shared/baseComponents/buttons/roundButton/roundButton.component';
import { RoundButtonStyle } from '~/_shared/baseComponents/buttons/roundButton/roundButton.styles';
import { TextInputComponent } from '~/_shared/baseComponents/inputs';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import { HeightTransitionComponent } from '~/_shared/components/heightTransition/heightTransition.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import { isTextEmpty } from '~/_shared/utils/text/text.helpers';
import type { BoundaryGroupPermissions } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { BoundaryListingContainer } from '../../boundaryListing/boundaryListing.container';

const itemStyle = (theme: Theme) => css({
  borderBottom: `1px solid ${theme.lineColors.basePrimary}`,
  marginBottom: -1,
});

const headerStyle = ({ isActive, borderTopHidden }: {isActive: boolean; borderTopHidden: boolean}) => (theme: Theme) => css({
  alignItems: 'center',
  background: isActive ? theme.accordionColors.headingBackgroundActive : theme.accordionColors.headingBackground,
  border: borderTopHidden && !isActive ? 'none' : `solid ${isActive ? theme.accordionColors.headingBorderActive : theme.accordionColors.borderColor}`,
  boxSizing: 'border-box',
  borderWidth: isActive ? '1px 0' : '1px 0 0',
  display: 'flex',
  height: ACCORDION_HEADER_SIZE_MEDIUM,
  justifyContent: 'space-between',
  marginTop: isActive && !borderTopHidden ? 1 : 0, //to negate the -1px of itemStyle so borderTop is shown
  padding: '4px 8px 4px 14px',

  '&:hover': {
    background: isActive ? theme.accordionColors.headingBackgroundActiveHover : theme.accordionColors.headingBackgroundHover,
  },
});

const headingStyle = css({
  flex: 1,
  marginRight: 8,
  overflow: 'hidden',
});

const headingPrimaryStyle = css({
  fontWeight: 400,
  display: 'block',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const headingSecondaryStyle = (theme: Theme) => css({
  color: theme.textColors.secondary,
  fontSize: '12px',
  margin: 0,
});

const buttonStyle = css({
  border: 'none',
  '&:hover': {
    border: 'none',
  },
  marginLeft: 6,
});

const expandButtonStyle = ({ isExpanded }: { isExpanded: boolean }) => (theme: Theme) => css({
  color: theme.textColors.primary,
  transform: `rotate(${isExpanded ? 0 : '180deg'})`,
  transition: 'transform .25s',
  fontSize: '14px',
  boxShadow: 'none',
  '&, &:hover': {
    background: 'none',
  },
});

const editNameInputStyle = css({
  fontSize: '14px',
  height: 30,
});

const mapsCountButtonStyle = css({
  fontSize: '12px',
  opacity: 1,
  padding: '6px 4px',
  minWidth: 75,
  boxSizing: 'border-box',
});

const removeButtonWrapperStyle = css({
  display: 'inline-block',
});

type BoundariesListingItemProps = Readonly<{
  boundaryGroupId: number;
  boundaryGroupPermissions: BoundaryGroupPermissions;
  className?: string;
  index: number;
  isLoading: boolean;
  mapsCount: number;
  name: string;

  onEditName: (newName: string) => void;
  onExpandClick: () => void;
  onRemove: () => void;
}>;

const BoundaryGroupsListingItemComponent: FC<BoundariesListingItemProps> = (props) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showItems, setShowItems] = useState(false);
  const [editingName, setEditingName] = useState(props.name);
  const [t] = useTranslation();

  const { boundaryIsEditable, boundaryOwnerName } = props.boundaryGroupPermissions;

  const startEditingItem = () => {
    setEditingName(props.name);
    setIsEditing(true);
  };

  const save = () => {
    if (isTextEmpty(editingName)) {
      return;
    }

    props.onEditName(editingName);
    setIsEditing(false);
  };

  const discard = () => {
    setEditingName(props.name);
    setIsEditing(false);
  };

  const toggleShowItems = () => {
    setShowItems(showItems => !showItems);
  };

  const isSubmitDisabled = props.isLoading || isTextEmpty(editingName) || editingName === props.name;
  const nonEditableTooltip = !boundaryIsEditable ? t('The owner of the territory ({{ownerName}}) doesn\'t allow editing of this territory group', { ownerName: boundaryOwnerName }) : null;
  const nonRemovableTooltip = !boundaryIsEditable ? t('The owner of the territory ({{ownerName}}) doesn\'t allow the removal of this territory group', { ownerName: boundaryOwnerName }) : null;

  return (
    <div css={itemStyle}>
      <div
        className={props.className}
        css={headerStyle({ isActive: showItems, borderTopHidden: props.index === 0 })}
      >
        <div css={headingStyle}>
          {!isEditing ? (
            <div>
              <strong css={headingPrimaryStyle}>
                {props.name}
              </strong>

              <p css={headingSecondaryStyle}>{t('territory group')}</p>
            </div>
          ) : (
            <TextInputComponent
              css={editNameInputStyle}
              onChange={setEditingName}
              value={editingName}
            />
          )}
        </div>

        <div>
          <TooltipComponent
            tooltipContent={t('Territory group used on {{count}} map', { count: props.mapsCount })}
          >
            <ButtonComponent
              buttonStyle={ButtonStyle.Custom} // change to Primary style once the onClick is implemented
              css={mapsCountButtonStyle}
              text={t('{{count}} Map', { count: props.mapsCount })}
              isDisabled
            />
          </TooltipComponent>

          {isEditing && (
            <>
              <RoundButtonComponent
                css={[buttonStyle]}
                buttonStyle={isSubmitDisabled ? RoundButtonStyle.Disabled : RoundButtonStyle.Confirm}
                size={RoundButtonSize.Small}
                icon={faCheck}
                isDisabled={isSubmitDisabled}
                onClick={save}
              />

              <RoundButtonComponent
                css={[buttonStyle]}
                buttonStyle={RoundButtonStyle.Danger}
                size={RoundButtonSize.Small}
                icon={faXmark}
                onClick={discard}
              />
            </>
          )}

          {!isEditing && (
            <RoundButtonComponent
              css={buttonStyle}
              size={RoundButtonSize.Small}
              icon={faPencil}
              onClick={startEditingItem}
              isDisabled={!boundaryIsEditable}
              buttonStyle={!boundaryIsEditable ? RoundButtonStyle.Disabled : RoundButtonStyle.Primary}
              tooltipLabel={nonEditableTooltip}
            />
          )}

          <span css={removeButtonWrapperStyle}>
            <RoundButtonComponent
              tooltipLabel={nonRemovableTooltip}
              css={buttonStyle}
              size={RoundButtonSize.Small}
              icon={faTrash}
              isDisabled={!boundaryIsEditable}
              buttonStyle={!boundaryIsEditable ? RoundButtonStyle.Disabled : RoundButtonStyle.Danger}
              onClick={props.onRemove}
            />
          </span>

          <RoundButtonComponent
            css={[buttonStyle, expandButtonStyle({ isExpanded: showItems })]}
            size={RoundButtonSize.Small}
            icon={faCaretUp}
            onClick={() => {
              toggleShowItems();
              props.onExpandClick();
            }}
          />
        </div>
      </div>

      <HeightTransitionComponent animateOnMount>
        {showItems && (
          <BoundaryListingContainer boundaryGroupId={props.boundaryGroupId} />
        )}
      </HeightTransitionComponent>
    </div>
  );
};

type BoundariesListingItemPureComponentProps = Readonly<{
  boundaryGroupId: number;
  boundaryGroupPermissions: BoundaryGroupPermissions;
  className?: string;
  index: number;
  isLoading: boolean;
  mapsCount: number;
  name: string;
  itemId: number;

  onEditName: (itemId: number, newName: string) => void;
  onExpandClick?: (itemId: number) => void;
  onRemove: (itemId: number) => void;
}>;

const pureComponent = memo((props: BoundariesListingItemPureComponentProps) => {
  const { onEditName, onExpandClick, onRemove, itemId, ...restProps } = props;

  return (
    <BoundaryGroupsListingItemComponent
      {...restProps}
      onEditName={(newName: string) => onEditName(itemId, newName)}
      onExpandClick={() => onExpandClick?.(itemId)}
      onRemove={() => onRemove(itemId)}
    />
  );
});
export { pureComponent as BoundaryGroupsListingItemComponent };
